import { getAccessToken } from 'utils/accessToken';
import { getOpParams } from 'utils/getOpParams';

import { useOpQuery } from './useOpQuery';

interface UseIdentityAndUserParams {
  /** This will be passed direct to queryString options of `'describeUser'` endpoint */
  describeUserOptions?: string;
}

/**
 * Hook to get the current logged in identity and user
 */
export const useIdentityAndUser = ({
  describeUserOptions,
}: UseIdentityAndUserParams = {}) => {
  const { orgId } = getOpParams();
  const accessToken = getAccessToken();

  const {
    data: tokenData,
    isLoading: isLoadingToken,
    isPending: isPendingToken,
    isFetching: isFetchingToken,
  } = useOpQuery({
    apiEndpointName: 'describeAccessToken',
    parameters: [accessToken],
    select: (data) => data?.json?.data,
  });

  const {
    data: identityData,
    isLoading: isLoadingIdentity,
    isPending: isPendingIdentity,
    isFetching: isFetchingIdentity,
  } = useOpQuery({
    apiEndpointName: 'describeIdentity',
    parameters: [tokenData?.identity?.id!],
    select: (data) => data?.json?.data,
  });

  const currentUserId = tokenData?.tokenScopeList?.find(
    (tokenScope) => tokenScope.org?.id === orgId,
  )?.user?.id;

  const {
    data: userData,
    isLoading: isLoadingUser,
    isPending: isPendingUser,
    isFetching: isFetchingUser,
  } = useOpQuery({
    apiEndpointName: 'describeUser',
    parameters: describeUserOptions
      ? [orgId, currentUserId!, { options: describeUserOptions }]
      : [orgId, currentUserId!],
    select: (data) => data?.json?.data,
  });

  return {
    /** data returned from describeIdentity */
    identity: identityData,
    /** data returned from describeUser */
    user: userData,
    /** data returned from describeAccessToken */
    token: tokenData,
    /** logged user identity id */
    identityId: tokenData?.identity?.id,
    /** current user id */
    userId: currentUserId,
    /** if any call is still loading */
    isLoading: isLoadingToken || isLoadingIdentity || isLoadingUser,
    /** if any call is still pending */
    isPending: isPendingToken || isPendingIdentity || isPendingUser,
    /** if any call is still fetching */
    isFetching: isFetchingToken || isFetchingIdentity || isFetchingUser,
  };
};
